/**
 * NodeTemplateEditModal
 * Wrapper that resolves DataTemplateIDs for the three records behind a node type,
 * then delegates editing to EditDataTemplateForm and cloning to CloneDataTemplateDialog.
 *
 *  Tab 1 – Node Template (TypeID 13)  : resolved via by-id (or code-search fallback)
 *  Tab 2 – Node Type Template (TypeID 14): lazy-resolved by code match, then by-id
 *  Tab 3 – Node Category Template (TypeID 15): lazy-resolved by category code, then by-id
 */

import React, { useState, useEffect, useRef } from 'react';
import { NodeType } from '@flow-studio/types';
import { ModalPortal } from './ModalPortal';
import { JsonEditor } from '../Shared/JsonEditor';
import {
  NodeTemplateApiClient,
  DataTemplateRecord,
} from '../../../../../../NodeTemplates/ApiClient/NodeTemplateApiClient';
import {
  EditDataTemplateForm,
  CloneDataTemplateDialog,
} from '../../../../../../DataTemplates/index';
import { NODE_TEMPLATE_CLIENT_OPTIONS } from '@flow-studio/store';

const nodeTemplateApiClient = new NodeTemplateApiClient(NODE_TEMPLATE_CLIENT_OPTIONS);

// Resolve a DataTemplateRecord by searching ContentData for a matching code field
async function resolveByCode(
  typeId: 13 | 14 | 15,
  matchCode: string
): Promise<DataTemplateRecord | null> {
  const records = await nodeTemplateApiClient.fetchRawRecords(typeId);
  return records.find(r => codeOf(r) === matchCode) ?? null;
}

// ─── Tab definitions ─────────────────────────────────────────────────────────

type TabId = 'nodeTemplate' | 'nodeTypeTemplate' | 'nodeCategoryTemplate';

const TABS: { id: TabId; label: string; typeId: 13 | 14 | 15; description: string }[] = [
  {
    id: 'nodeTemplate',
    label: 'Node Template',
    typeId: 13,
    description: 'Primary node definition — ports, schemas, display metadata and behaviour (TypeID 13).',
  },
  {
    id: 'nodeTypeTemplate',
    label: 'Node Type Template',
    typeId: 14,
    description: 'Type-level template linked by code — category defaults and shared type behaviour (TypeID 14).',
  },
  {
    id: 'nodeCategoryTemplate',
    label: 'Node Category Template',
    typeId: 15,
    description: 'Category template — category-wide settings and defaults (TypeID 15).',
  },
];

// ─── Per-tab resolved state ───────────────────────────────────────────────────

interface TabResolved {
  dataTemplateID: number | null;
  record: DataTemplateRecord | null; // kept for clone dialog display name
  resolving: boolean;
  resolved: boolean;
  resolveError: string | null;
}

const initTab = (resolving = false): TabResolved => ({
  dataTemplateID: null, record: null,
  resolving, resolved: false, resolveError: null,
});

function codeOf(record: DataTemplateRecord): string {
  try { return JSON.parse(record.contentData ?? '{}').code ?? ''; } catch { return ''; }
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  nodeType: NodeType;
  onClose: () => void;
}

export const NodeTemplateEditModal: React.FC<Props> = ({ nodeType, onClose }) => {
  const [activeTab, setActiveTab] = useState<TabId>('nodeTemplate');
  const [tabs, setTabs] = useState<Record<TabId, TabResolved>>({
    nodeTemplate: initTab(true), // start resolving immediately
    nodeTypeTemplate: initTab(),
    nodeCategoryTemplate: initTab(),
  });
  const [showClone, setShowClone] = useState(false);

  const tab1AbortRef = useRef<AbortController | null>(null);
  const lazyAbortRef = useRef<AbortController | null>(null);

  const patchTab = (id: TabId, delta: Partial<TabResolved>) =>
    setTabs(prev => ({ ...prev, [id]: { ...prev[id], ...delta } }));

  // ── Tab 1: resolve DataTemplateID on mount ────────────────────────────────
  useEffect(() => {
    const ctrl = new AbortController();
    tab1AbortRef.current = ctrl;

    // nodeType.id is the DataTemplateID injected during toolbar load (from the DB row).
    // It is only present when the toolbar loaded from the API. If undefined, the node
    // was loaded from the static JSON fallback (no DB record exists yet).
    const knownId = (nodeType as any).id as number | undefined;

    (async () => {
      try {
        if (knownId) {
          patchTab('nodeTemplate', { dataTemplateID: knownId, resolving: false, resolved: true });
          return;
        }

        if (!knownId) {
          // Node loaded from static JSON fallback — no DataTemplateID on the nodeType object.
          // Try a code-based search as a last resort in case the record exists but the ID wasn't propagated.
          const records = await nodeTemplateApiClient.fetchRawRecords(13);
          if (ctrl.signal.aborted) return;
          const found = records.find(r => codeOf(r) === nodeType.code) ?? null;
          if (found) {
            patchTab('nodeTemplate', {
              dataTemplateID: found.dataTemplateID,
              record: found,
              resolving: false,
              resolved: true,
            });
            return;
          }
          // Record genuinely not in the database — most likely the SQL seed hasn't been run.
          patchTab('nodeTemplate', {
            resolving: false, resolved: true,
            resolveError: `DATABASE_MISSING:${nodeType.code}`,
          });
          return;
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        patchTab('nodeTemplate', {
          resolving: false, resolved: true,
          resolveError: err instanceof Error ? err.message : 'Failed to resolve template.',
        });
      }
    })();

    return () => { ctrl.abort(); };
  }, []);

  // ── Tabs 2 & 3: lazy resolve when first activated ────────────────────────
  useEffect(() => {
    if (activeTab === 'nodeTemplate') return;
    const t = tabs[activeTab];
    if (t.resolved || t.resolving) return;

    const tabDef = TABS.find(x => x.id === activeTab)!;
    patchTab(activeTab, { resolving: true });

    lazyAbortRef.current?.abort();
    const ctrl = new AbortController();
    lazyAbortRef.current = ctrl;

    const matchCode = activeTab === 'nodeTypeTemplate' ? nodeType.code : nodeType.category;

    (async () => {
      try {
        const records = await nodeTemplateApiClient.fetchRawRecords(tabDef.typeId);
        if (ctrl.signal.aborted) return;
        const found = records.find(r => codeOf(r) === matchCode) ?? null;
        patchTab(activeTab, {
          dataTemplateID: found?.dataTemplateID ?? null,
          record: found,
          resolving: false,
          resolved: true,
          resolveError: found ? null : `No record with code "${matchCode}" (TypeID ${tabDef.typeId}).`,
        });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        patchTab(activeTab, {
          resolving: false, resolved: true,
          resolveError: err instanceof Error ? err.message : 'Failed to load records.',
        });
      }
    })();

    return () => { ctrl.abort(); };
  }, [activeTab]);

  const t = tabs[activeTab];
  const tabMeta = TABS.find(x => x.id === activeTab)!;

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: '8px 20px', fontSize: '12px',
    fontWeight: active ? 600 : 400,
    color: active ? '#fff' : 'rgba(255,255,255,0.5)',
    background: active ? 'rgba(48,155,72,0.25)' : 'transparent',
    border: 'none', borderBottom: active ? '2px solid #309B48' : '2px solid transparent',
    cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
  });

  return (
    <ModalPortal>
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'auto',
        }}
        onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="nodrag nopan"
          style={{
            background: '#0f172a', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: '12px', width: '92vw', maxWidth: '1400px', height: '88vh',
            display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 60px rgba(0,0,0,0.7)', overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
          }}>
            <div>
              <span style={{ fontSize: '14px', fontWeight: 700, color: '#fff' }}>Node Template Editor</span>
              <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '10px' }}>
                {nodeType.displayName}
                {nodeType.code !== nodeType.displayName && (
                  <span style={{ color: '#475569', marginLeft: '6px' }}>· {nodeType.code}</span>
                )}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Clone button — only shown when tab 1 is resolved with a valid ID */}
              {activeTab === 'nodeTemplate' && t.dataTemplateID != null && !showClone && (
                <button
                  onClick={() => setShowClone(true)}
                  style={{
                    padding: '5px 14px', borderRadius: '6px', fontSize: '12px',
                    border: '1px solid rgba(48,155,72,0.4)',
                    background: 'rgba(48,155,72,0.1)',
                    color: '#6ee7b7', cursor: 'pointer',
                  }}
                  title="Clone this template to create a new editable copy"
                >
                  Clone
                </button>
              )}
              <button
                onClick={onClose}
                style={{
                  padding: '5px 14px', borderRadius: '6px', fontSize: '12px',
                  border: '1px solid rgba(255,255,255,0.15)', background: 'transparent',
                  color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 16px', flexShrink: 0 }}>
            {TABS.map(tab => (
              <button key={tab.id} style={tabBtnStyle(activeTab === tab.id)} onClick={() => setActiveTab(tab.id)}>
                {tab.label}
                {!tabs[tab.id].resolved && !tabs[tab.id].resolving && tab.id !== 'nodeTemplate' && (
                  <span style={{ marginLeft: '5px', color: 'rgba(255,255,255,0.2)', fontSize: '9px' }}>●</span>
                )}
              </button>
            ))}
          </div>

          {/* Description */}
          <div style={{ padding: '8px 24px 0', flexShrink: 0 }}>
            <p style={{ margin: 0, fontSize: '11px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>
              {tabMeta.description}
              {t.dataTemplateID != null && (
                <span style={{ color: 'rgba(255,255,255,0.18)', marginLeft: '8px' }}>
                  DataTemplateID: {t.dataTemplateID}
                </span>
              )}
            </p>
          </div>

          {/* Body */}
          <div style={{ flex: 1, minHeight: 0, padding: '12px 24px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {t.resolving && (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>Loading…</span>
              </div>
            )}

            {!t.resolving && t.resolveError && (() => {
              const isMissing = t.resolveError.startsWith('DATABASE_MISSING:');
              const missingCode = isMissing ? t.resolveError.slice('DATABASE_MISSING:'.length) : null;
              const isNotFound = !isMissing && (t.resolveError.includes('No record with code') || t.resolveError.includes('No Node Template'));
              const sqlTarget = activeTab === 'nodeCategoryTemplate' ? nodeType.category : (missingCode ?? nodeType.code);

              return (
                <div style={{
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: '8px', padding: '20px 24px', color: '#fca5a5', fontSize: '13px',
                  lineHeight: 1.7,
                }}>
                  {isMissing ? (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: '10px' }}>
                        Database record not found for node <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: '4px' }}>{missingCode}</code>
                      </div>
                      <div style={{ color: 'rgba(252,165,165,0.75)', fontSize: '12px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          This node is currently loaded from the <strong>static JSON fallback</strong> — it has no record in the <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: '4px' }}>Template_DataTemplates</code> table yet.
                        </div>
                        <div>
                          Find and run the seed script:{' '}
                          <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace' }}>
                            Template_DataTemplates_*_{sqlTarget}.data.sql
                          </code>
                          {' '}in <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace' }}>BizFirstFiV3DB/dbo/Data/DataTemplates/NodeTemplates</code>,
                          then refresh the page.
                        </div>
                      </div>
                    </>
                  ) : isNotFound ? (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: '10px' }}>{t.resolveError}</div>
                      <div style={{ color: 'rgba(252,165,165,0.7)', fontSize: '12px' }}>
                        Find and run the seed script:{' '}
                        <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>
                          Template_DataTemplates_*_{sqlTarget}.data.sql
                        </code>
                        {' '}in <code style={{ background: 'rgba(0,0,0,0.3)', padding: '1px 6px', borderRadius: '4px', fontFamily: 'monospace' }}>BizFirstFiV3DB/dbo/Data/DataTemplates/NodeTemplates</code>.
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600, marginBottom: '6px' }}>{t.resolveError}</div>
                      <div style={{ color: 'rgba(252,165,165,0.7)', fontSize: '12px' }}>Check the browser console for details.</div>
                    </>
                  )}
                </div>
              );
            })()}

            {!t.resolving && !t.resolveError && t.dataTemplateID != null && (
              <EditDataTemplateForm
                key={t.dataTemplateID}
                dataTemplateID={t.dataTemplateID}
                JsonEditor={JsonEditor}
              />
            )}
          </div>

        </div>
      </div>

      {/* Clone dialog — rendered over the edit modal */}
      {showClone && t.dataTemplateID != null && (
        <CloneDataTemplateDialog
          sourceDataTemplateID={t.dataTemplateID}
          sourceName={t.record?.templateName ?? nodeType.displayName}
          onCloned={() => { /* user can open the new ID in a future session */ }}
          onClose={() => setShowClone(false)}
        />
      )}
    </ModalPortal>
  );
};
